import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../core/AppContext';
import { PersonaProvider, usePersona } from './PersonaContext';
import PersonaErrorBoundary from './PersonaErrorBoundary';
import VoiceCapture from './VoiceCapture';

const label = { display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: '#333' };
const input = { width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' };
// Friendly, engine-neutral job stage labels (never show model/engine names in chat).
const STAGE_LABELS = { QUEUED: 'Queued', STARTING: 'Starting', RENDERING: 'Working', CLONING_VOICE: 'Generating voice',
  ANIMATING: 'Animating video', UPLOADING: 'Finishing', DONE: 'Done', FAILED: 'Failed' };
const stageLabel = (j) => (!j ? '' : (j.stage_label || STAGE_LABELS[j.stage] || STAGE_LABELS[j.status] || 'Working'));
const card = { backgroundColor: '#fff', borderRadius: '8px', padding: '18px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' };

// Defensive: never hand a non-string to React as a child (that blanks the page).
const safe = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch { return String(v); }
};

function PersonaStudio() {
  const { can } = useApp();
  const {
    personas, selectedId, status, busy, history, input: chatInput, audio, renderJob, models,
    pendingReply, renderLogs, mode,
    setInput, setMode, refreshPersonas, fetchModels, createPersona, updatePersona,
    deletePersona, uploadAsset, deleteAsset, selectPersona, sendMessage, pollJob,
    revealPending, fetchRenderLogs
  } = usePersona();

  const canUse = can('use_persona');
  // Persona editing (add/update/configure/assets/KB): the dedicated persona-editor
  // privilege OR a full config admin. Lets a "persona editor" manage personas
  // without the broader manage_config power.
  const canManage = can('manage_personas') || can('manage_config');
  // Studio-wide settings (voice-clone engine) and engine internals stay admin-only.
  const canManageConfig = can('manage_config');
  const chatEndRef = useRef(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [jobStatus, setJobStatus] = useState(null);
  const [tab, setTab] = useState('chat'); // 'chat' | 'admin' | 'logs'
  const [elapsed, setElapsed] = useState(0); // seconds since the render job started
  // Collapsible persona list (persisted) so the chat can use the full width.
  const [listCollapsed, setListCollapsed] = useState(() => {
    try { return localStorage.getItem('persona_list_collapsed') === '1'; } catch (e) { return false; }
  });
  const toggleList = () => setListCollapsed(v => {
    const n = !v;
    try { localStorage.setItem('persona_list_collapsed', n ? '1' : '0'); } catch (e) { /* ignore */ }
    return n;
  });
  // Studio-wide voice clone engine (admin only; never shown in chat).
  const [engine, setEngine] = useState('chatterbox');
  const [engineMsg, setEngineMsg] = useState('');
  const tokenHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
  // Knowledge documents (RAG grounding sources) for the selected persona.
  const [kbDocs, setKbDocs] = useState([]);
  const [kbMsg, setKbMsg] = useState('');
  const loadKbDocs = (pid) => {
    if (!pid) return;
    fetch(`/api/personas/${pid}/kb-docs`, { headers: tokenHeaders() })
      .then(r => r.json()).then(d => setKbDocs(Array.isArray(d.documents) ? d.documents : []))
      .catch(() => { /* ignore */ });
  };
  const uploadKbDoc = async (pid, file) => {
    if (!pid || !file) return;
    setKbMsg(`Indexing ${file.name}\u2026 (embedding may take a moment)`);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await fetch(`/api/personas/${pid}/kb-doc`, {
        method: 'POST', headers: tokenHeaders(),
        body: JSON.stringify({ b64, filename: file.name }),
      });
      const d = await res.json().catch(() => ({}));
      setKbMsg(d.status === 'SUCCESS' ? `Indexed ${file.name} (${d.doc_chunks || 0} chunks).`
        : `Could not index: ${safe(d.message || d.error || d.status)}`);
      loadKbDocs(pid);
    } catch (e) { setKbMsg('Upload failed.'); }
  };
  const deleteKbDoc = async (pid, source) => {
    setKbMsg(`Removing ${source}\u2026`);
    try {
      await fetch(`/api/personas/${pid}/kb-doc`, {
        method: 'DELETE', headers: tokenHeaders(), body: JSON.stringify({ source }),
      });
      setKbMsg('Removed.');
      loadKbDocs(pid);
    } catch (e) { setKbMsg('Remove failed.'); }
  };
  useEffect(() => {
    if (tab !== 'admin' || !canManageConfig) return;
    fetch('/api/persona-settings', { headers: tokenHeaders() })
      .then(r => r.json()).then(d => { if (d && d.voice_engine) setEngine(d.voice_engine); })
      .catch(() => { /* ignore */ });
  }, [tab, canManageConfig]);
  // Load the persona's indexed KB documents when the Admin tab opens.
  useEffect(() => {
    if (tab !== 'admin' || !selectedId) return;
    loadKbDocs(selectedId);
    setKbMsg('');
    /* eslint-disable-next-line */
  }, [tab, selectedId]);
  const saveEngine = async (val) => {
    setEngine(val); setEngineMsg('Saving…');
    try {
      const r = await fetch('/api/persona-settings', { method: 'POST', headers: tokenHeaders(), body: JSON.stringify({ voice_engine: val }) });
      const d = await r.json().catch(() => ({}));
      setEngineMsg(r.ok && d.status === 'SUCCESS' ? 'Saved' : `Save failed: ${safe(d.error || r.status)}`);
    } catch (e) { setEngineMsg('Save failed'); }
  };

  useEffect(() => { if (canUse) { refreshPersonas(); fetchModels(); } /* eslint-disable-next-line */ }, [canUse]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history]);

  // Load render-job logs when the Logs tab is open for a persona, and refresh
  // while a render is active so the log updates live.
  useEffect(() => {
    if (tab !== 'logs' || !selected) return;
    fetchRenderLogs(selected.persona_id);
    const t = setInterval(() => fetchRenderLogs(selected.persona_id), 5000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [tab, selectedId, jobStatus && jobStatus.status]);

  useEffect(() => {
    if (!renderJob || !renderJob.job_id) return;
    let alive = true;
    const startedAt = Date.now();
    setElapsed(0);
    const timer = setInterval(() => { if (alive) setElapsed(Math.floor((Date.now() - startedAt) / 1000)); }, 1000);
    const tick = async () => {
      const j = await pollJob(renderJob.job_id);
      if (!alive) return;
      if (j) {
        setJobStatus(j);
        if (j.status === 'DONE') {
          clearInterval(timer);
          // Media is ready — reveal the held reply. Pass the whole job so the
          // context routes audio (mp3) vs video (mp4) by its result_kind.
          revealPending(j);
          return;
        }
        if (j.status === 'FAILED') {
          clearInterval(timer);
          // Render failed: reveal the text anyway (without video) so the user
          // still sees the reply, and the failure shows in the status/panel.
          revealPending(null);
          return;
        }
      }
      setTimeout(tick, 4000);
    };
    tick();
    return () => { alive = false; clearInterval(timer); };
  }, [renderJob, pollJob, revealPending]);

  const selected = personas.find(p => p.persona_id === selectedId) || null;

  if (!canUse) {
    return (
      <div style={{ ...card, marginTop: '10px', backgroundColor: '#f8d7da', borderLeft: '4px solid #dc3545', color: '#721c24', fontSize: '13px' }}>
        You do not have the <code>use_persona</code> privilege. Ask an admin to grant it in the Admin &rarr; Users panel.
      </div>
    );
  }

  const tabBtn = (id, text) => (
    <button onClick={() => setTab(id)} style={{
      padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
      borderBottom: tab === id ? '3px solid #0073bb' : '3px solid transparent',
      backgroundColor: 'transparent', color: tab === id ? '#0073bb' : '#718096',
    }}>{text}</button>
  );

  return (
    <div style={{ marginTop: '10px' }}>
      <style>{`@keyframes personaPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
      {/* Header */}
      <div style={{ ...card, marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ fontSize: '38px' }}>👤</div>
            <div>
              <h2 style={{ color: '#0f2027', margin: 0 }}>TSA Persona Studio</h2>
              <p style={{ color: '#718096', margin: '4px 0 0 0', fontSize: '13px' }}>
                Multi-persona digital twins — Bedrock chat{models.selected ? ` · model: ${models.selected}` : ''}
              </p>
            </div>
          </div>
          {canManage && (
            <button onClick={() => setShowAdd(v => !v)} style={{ padding: '9px 16px', backgroundColor: '#0073bb', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
              {showAdd ? 'Cancel' : '+ Add Persona'}
            </button>
          )}
        </div>
        {showAdd && canManage && (
          <div style={{ marginTop: '14px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input style={{ ...input, flex: 1 }} placeholder="New persona name (e.g. 'Eben – CTO Twin')" value={newName} onChange={e => setNewName(e.target.value)} />
            <button
              onClick={async () => { const p = await createPersona({ name: newName }); if (p) { setNewName(''); setShowAdd(false); selectPersona(p.persona_id); } }}
              disabled={!newName.trim()}
              style={{ padding: '8px 16px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
              Create
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
        {/* LEFT: persona list (collapsible to an avatar strip) */}
        <div style={{ flex: listCollapsed ? '0 0 64px' : '0 0 240px', ...card, padding: listCollapsed ? '10px 6px' : card.padding, transition: 'flex-basis 0.2s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: listCollapsed ? 'center' : 'space-between', marginBottom: '12px' }}>
            {!listCollapsed && <h3 style={{ margin: 0, color: '#0f2027', fontSize: '15px' }}>Personas ({personas.length})</h3>}
            <button onClick={toggleList} title={listCollapsed ? 'Show personas' : 'Hide personas'}
              style={{ border: '1px solid #cbd5e0', background: '#fff', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', color: '#0073bb', fontSize: '12px' }}>
              {listCollapsed ? '▶' : '◀'}
            </button>
          </div>
          {!listCollapsed && personas.length === 0 && <p style={{ color: '#a0aec0', fontSize: '13px' }}>No personas yet. {canManage ? 'Click "Add Persona".' : 'Ask an admin to create one.'}</p>}
          {personas.map(p => (
            <div key={p.persona_id} title={safe(p.name)}
              onClick={() => selectPersona(p.persona_id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: listCollapsed ? 'center' : 'flex-start', gap: '10px',
                padding: listCollapsed ? '4px' : '8px', borderRadius: '6px', cursor: 'pointer', marginBottom: '6px',
                border: selectedId === p.persona_id ? '2px solid #0073bb' : '1px solid #e2e8f0',
                backgroundColor: selectedId === p.persona_id ? '#eef6fc' : '#fff',
              }}>
              {p.image_url
                ? <img src={p.image_url} alt="" style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#cbd5e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>👤</div>}
              {!listCollapsed && (
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1a202c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{safe(p.name)}</div>
                  <div style={{ fontSize: '10px', color: '#718096' }}>{p.image_key ? '🖼️' : '—'} {p.voice_key ? '🎙️' : '—'}</div>
                </div>
              )}
              {!listCollapsed && canManage && (
                <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete persona '${p.name}'?`)) deletePersona(p.persona_id); }}
                  style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer', fontSize: '15px' }} title="Delete persona">🗑️</button>
              )}
            </div>
          ))}
        </div>

        {/* RIGHT: tabbed workspace for the selected persona */}
        <div style={{ flex: '1 1 0', minWidth: 0, ...card, padding: 0 }}>
          {!selected && <p style={{ color: '#a0aec0', fontSize: '13px', textAlign: 'center', padding: '50px' }}>Select a persona.</p>}
          {selected && (
            <>
              {/* Tab bar */}
              <div style={{ borderBottom: '1px solid #e2e8f0', paddingLeft: '8px' }}>
                {tabBtn('chat', '💬 Chat')}
                {tabBtn('admin', '⚙️ Admin')}
                {tabBtn('logs', '📋 Render Logs')}
              </div>

              {/* CHAT TAB */}
              {tab === 'chat' && (
                <div style={{ padding: '18px', display: 'flex', gap: '16px' }}>
                  {/* Persona portrait */}
                  <div style={{ flex: '0 0 160px', textAlign: 'center' }}>
                    {selected.image_url
                      ? <img src={selected.image_url} alt={safe(selected.name)} style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '10px', border: '2px solid #e2e8f0' }} />
                      : <div style={{ width: '150px', height: '150px', borderRadius: '10px', background: '#cbd5e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '56px' }}>👤</div>}
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f2027', marginTop: '8px' }}>{safe(selected.name)}</div>
                    {audio && (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '10px', color: '#555', marginBottom: '3px' }}>🔊 Voice reply</div>
                        <audio key={audio} src={audio} controls autoPlay style={{ height: '32px', width: '150px' }} />
                      </div>
                    )}
                    {jobStatus && (
                      <div style={{ marginTop: '10px', fontSize: '11px' }}>
                        🎬 Status: <strong>{stageLabel(jobStatus)}</strong>
                        {(jobStatus.status === 'QUEUED' || jobStatus.status === 'RENDERING') && (
                          <div style={{ color: '#718096', marginTop: '2px' }}>
                            {Math.floor(elapsed / 60)}m {elapsed % 60}s elapsed
                            <div style={{ fontSize: '10px', color: '#a0aec0', marginTop: '2px' }}>
                              First reply can take longer while the studio warms up; later replies are quicker.
                            </div>
                          </div>
                        )}
                        {jobStatus.status === 'DONE' && (
                          <>
                            {jobStatus.elapsed_sec ? <div style={{ color: '#28a745' }}>Done in {Math.floor(jobStatus.elapsed_sec / 60)}m {jobStatus.elapsed_sec % 60}s</div> : null}
                          </>
                        )}
                        {jobStatus.status === 'FAILED' && (
                          <div style={{ color: '#b00020', marginTop: '2px' }}>
                            Couldn't generate this reply. Please try again.
                            {canManageConfig && jobStatus.detail && <div style={{ fontSize: '10px', color: '#b00020', whiteSpace: 'pre-wrap', marginTop: '2px', maxHeight: '120px', overflow: 'auto' }}>{safe(jobStatus.detail)}</div>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Chat column */}
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '14px', height: 'calc(100vh - 380px)', minHeight: '420px', overflowY: 'auto', backgroundColor: '#fbfcfe', marginBottom: '12px' }}>
                      {history.length === 0 && <p style={{ color: '#a0aec0', fontSize: '13px', textAlign: 'center', marginTop: '120px' }}>Say something to {safe(selected.name)}…</p>}
                      {history.map((m, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                          <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: '12px', fontSize: '14px', lineHeight: 1.45, whiteSpace: 'pre-wrap', backgroundColor: m.role === 'user' ? '#0073bb' : '#edf2f7', color: m.role === 'user' ? '#fff' : '#1a202c' }}>
                            {safe(m.content)}
                            {m.video && (
                              <video key={m.video} src={m.video} controls autoPlay style={{ display: 'block', width: '100%', maxWidth: '260px', borderRadius: '8px', marginTop: '8px' }} />
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Hide-until-ready: while a reply is held pending its media, show a
                          thinking indicator in place of the assistant bubble. */}
                      {pendingReply && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
                          <div style={{ maxWidth: '80%', padding: '8px 12px', borderRadius: '12px', fontSize: '13px', backgroundColor: '#edf2f7', color: '#718096', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="persona-thinking-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0073bb', display: 'inline-block', animation: 'personaPulse 1s ease-in-out infinite' }} />
                            {mode === 'cloned_lipsync'
                              ? `${stageLabel(jobStatus) || 'Preparing'}… response appears when the video is ready`
                              : mode === 'cloned_voice'
                                ? `${stageLabel(jobStatus) || 'Generating voice'}… response appears when the cloned voice is ready`
                                : 'Preparing voice… response appears when the voice is ready'}
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Reply mode (GLOBAL for all personas): AI Voice / Cloned Voice /
                        Cloned Voice + Lip-Sync. The reply is held until the selected
                        media is ready, then revealed and played. */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#718096', marginBottom: '5px' }}>Response mode (applies to all personas)</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[
                          { id: 'ai_voice',       label: '🔊 AI Voice',             hint: 'Preset voice, fast. Reveal when voice is ready', needs: 'none' },
                          { id: 'cloned_voice',   label: '🧬 Cloned Voice',          hint: "Clone the persona's voice (audio). Reveal when the clone is ready", needs: 'voice' },
                          { id: 'cloned_lipsync', label: '🎬 Voice + Lip-Sync (~45s)', hint: 'Cloned voice + animated talking head. Reveal when the video is ready (~45s)', needs: 'both' },
                          { id: 'echomimic_lipsync', label: '🎞️ Realistic (~30–45s)', hint: 'Most realistic talking head. Reveal when the video is ready (~30–45s)', needs: 'both' },
                        ].map(opt => {
                          const active = mode === opt.id;
                          const needsAssets =
                            (opt.needs === 'voice' && !selected.voice_key) ||
                            (opt.needs === 'both' && (!selected.image_key || !selected.voice_key));
                          return (
                            <button key={opt.id}
                              onClick={() => setMode(opt.id)}
                              title={needsAssets ? 'Missing required asset(s) — add them in the Admin tab' : opt.hint}
                              style={{
                                padding: '7px 12px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
                                borderRadius: '6px', border: active ? '2px solid #0073bb' : '1px solid #cbd5e0',
                                background: active ? '#eef6fc' : '#fff', color: active ? '#0073bb' : '#4a5568',
                                opacity: needsAssets ? 0.55 : 1,
                              }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {mode === 'cloned_voice' && !selected.voice_key && (
                        <div style={{ fontSize: '11px', color: '#b7791f', marginTop: '5px' }}>
                          ⚠️ Cloned Voice needs a voice sample — add one in the Admin tab.
                        </div>
                      )}
                      {mode === 'cloned_lipsync' && (!selected.image_key || !selected.voice_key) && (
                        <div style={{ fontSize: '11px', color: '#b7791f', marginTop: '5px' }}>
                          ⚠️ Lip-Sync needs both a face image and a voice sample — add them in the Admin tab.
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" value={chatInput} onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !busy) { e.preventDefault(); sendMessage(); } }}
                        placeholder={`Ask ${safe(selected.name)} anything...`} disabled={busy} style={{ ...input, flex: 1, padding: '11px', fontSize: '14px' }} />
                      <button onClick={sendMessage} disabled={busy || !chatInput.trim()}
                        style={{ padding: '8px 18px', backgroundColor: busy ? '#90cdf4' : '#0073bb', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: busy ? 'default' : 'pointer' }}>
                        {busy ? '…' : 'Talk'}
                      </button>
                    </div>
                    {status && <p style={{ fontSize: '12px', color: '#555', marginTop: '10px', marginBottom: 0, wordBreak: 'break-all' }}>{safe(status)}</p>}
                  </div>
                </div>
              )}

              {/* ADMIN TAB */}
              {tab === 'admin' && (
                <div style={{ padding: '18px', opacity: canManage ? 1 : 0.6 }}>
                  {canManageConfig && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', marginBottom: '16px', background: '#f8fafc' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>Studio settings (all personas)</div>
                      <label style={label}>Voice clone engine</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select value={engine} onChange={e => saveEngine(e.target.value)} style={{ ...input, width: 'auto' }}>
                          <option value="chatterbox">Chatterbox (default)</option>
                          <option value="xtts">XTTS v2</option>
                        </select>
                        {engineMsg && <span style={{ fontSize: '11px', color: '#555' }}>{engineMsg}</span>}
                      </div>
                      <div style={{ fontSize: '10px', color: '#718096', marginTop: '4px' }}>
                        Used for Cloned Voice and Lip-Sync replies. Chat users never see which engine is used. If Chatterbox fails, the reply falls back to XTTS.
                      </div>
                    </div>
                  )}
                  <label style={label}>Name</label>
                  <input id="p-name" style={{ ...input, marginBottom: '10px' }} defaultValue={safe(selected.name)} disabled={!canManage} key={`name-${selected.persona_id}`} />

                  <label style={label}>System Prompt (who this persona is)</label>
                  <textarea id="p-prompt" rows={3} style={{ ...input, resize: 'vertical', marginBottom: '10px' }} defaultValue={safe(selected.system_prompt)} disabled={!canManage} key={`pr-${selected.persona_id}`} />

                  <label style={label}>Context / Knowledge Base</label>
                  <textarea id="p-kb" rows={4} style={{ ...input, resize: 'vertical', marginBottom: '10px' }} defaultValue={safe(selected.knowledge_base)} disabled={!canManage} key={`kb-${selected.persona_id}`} />

                  <label style={label}>Voice (preset for quick reply)</label>
                  <select id="p-voice" style={{ ...input, marginBottom: '10px' }} defaultValue={selected.voice} disabled={!canManage} key={`v-${selected.persona_id}`}>
                    <option value="en-US-ChristopherNeural">en-US Christopher (M)</option>
                    <option value="en-US-JennyNeural">en-US Jenny (F)</option>
                    <option value="en-US-GuyNeural">en-US Guy (M)</option>
                    <option value="en-GB-RyanNeural">en-GB Ryan (M)</option>
                    <option value="en-GB-SoniaNeural">en-GB Sonia (F)</option>
                  </select>

                  {canManage && (
                    <button
                      onClick={() => updatePersona(selected.persona_id, {
                        name: document.getElementById('p-name').value,
                        system_prompt: document.getElementById('p-prompt').value,
                        knowledge_base: document.getElementById('p-kb').value,
                        voice: document.getElementById('p-voice').value,
                      })}
                      style={{ width: '100%', padding: '9px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '16px' }}>
                      Save Persona
                    </button>
                  )}

                  <div style={{ borderTop: '1px solid #eee', paddingTop: '14px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <label style={label}>Face Image</label>
                      {selected.image_url
                        ? <img src={selected.image_url} alt="" style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', borderRadius: '6px', marginBottom: '6px' }} />
                        : <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>none</div>}
                      {canManage && <input type="file" accept="image/*" style={{ fontSize: '11px' }} onChange={e => uploadAsset(selected.persona_id, 'image', e.target.files && e.target.files[0])} />}
                      {canManage && selected.image_key && <button onClick={() => deleteAsset(selected.persona_id, 'image')} style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}>Delete image</button>}
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                      <VoiceCapture
                        personaId={selected.persona_id}
                        voiceUrl={selected.voice_url}
                        canManage={canManage}
                        uploadAsset={uploadAsset}
                        deleteAsset={deleteAsset}
                        hasVoiceKey={!!selected.voice_key}
                      />
                    </div>
                  </div>
                  {/* Knowledge documents (RAG grounding sources) */}
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '14px', marginTop: '14px' }}>
                    <label style={label}>Knowledge Documents (RAG)</label>
                    <div style={{ fontSize: '11px', color: '#718096', marginBottom: '6px' }}>
                      Upload PDFs or text files to ground this persona's answers. Replies draw only
                      on these documents and the Knowledge Base field above.
                    </div>
                    {canManage && (
                      <input type="file" accept=".pdf,.txt,.md,.csv,.json" style={{ fontSize: '11px' }}
                        onChange={e => uploadKbDoc(selected.persona_id, e.target.files && e.target.files[0])} />
                    )}
                    {kbDocs.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        {kbDocs.map(d => (
                          <div key={d.source} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', padding: '3px 0' }}>
                            <span style={{ color: '#1a202c' }}>
                              {d.source === 'kb_text' ? '\ud83d\udcdd Knowledge Base field' : `\ud83d\udcc4 ${safe(String(d.source).replace(/^doc:/, ''))}`}
                              <span style={{ color: '#a0aec0' }}> ({d.chunks} chunks)</span>
                            </span>
                            {canManage && d.source !== 'kb_text' && (
                              <button onClick={() => deleteKbDoc(selected.persona_id, d.source)}
                                style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer', fontSize: '13px' }} title="Remove document">\ud83d\uddd1\ufe0f</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {kbMsg && <div style={{ fontSize: '11px', color: '#555', marginTop: '6px' }}>{safe(kbMsg)}</div>}
                  </div>
                  {!canManage && <p style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>Editing requires the <code>manage_personas</code> (or <code>manage_config</code>) privilege.</p>}
                  {status && <p style={{ fontSize: '12px', color: '#555', marginTop: '10px', wordBreak: 'break-all' }}>{safe(status)}</p>}
                </div>
              )}

              {/* RENDER LOGS TAB — voice-clone + lip-sync job history for this persona */}
              {tab === 'logs' && (
                <div style={{ padding: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, color: '#0f2027', fontSize: '15px' }}>Render Logs</h3>
                    <button onClick={() => fetchRenderLogs(selected.persona_id)} style={{ fontSize: '11px', padding: '5px 10px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>↻ Refresh</button>
                  </div>
                  <p style={{ fontSize: '11px', color: '#718096', marginTop: 0 }}>Voice-clone &amp; lip-sync job history for {safe(selected.name)}. Updates live while a render runs.</p>
                  {(!renderLogs || renderLogs.length === 0) && (
                    <p style={{ color: '#a0aec0', fontSize: '13px' }}>No render jobs yet. Enable “Render lip-sync video” in Chat and send a message.</p>
                  )}
                  {renderLogs && renderLogs.map((j) => {
                    const st = safe(j.status);
                    const color = st === 'DONE' ? '#28a745' : st === 'FAILED' ? '#b00020' : '#0073bb';
                    return (
                      <div key={safe(j.job_id)} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px', marginBottom: '8px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontFamily: 'monospace', color: '#4a5568' }}>{safe(j.job_id)}</span>
                          <span style={{ fontWeight: 'bold', color }}>{stageLabel(j)}</span>
                        </div>
                        <div style={{ color: '#718096', marginTop: '4px' }}>
                          Status: <strong style={{ color }}>{st}</strong>
                          {j.elapsed_sec ? ` · ${Math.floor(j.elapsed_sec / 60)}m ${j.elapsed_sec % 60}s` : ''}
                          {j.created_at ? ` · queued ${new Date(Number(j.created_at) * 1000).toLocaleString()}` : ''}
                        </div>
                        {canManageConfig && j.engine_used && (
                          <div style={{ marginTop: '4px', color: '#4a5568' }}>Engine (admins only): <strong>{safe(j.engine_used)}</strong></div>
                        )}
                        {j.detail && (
                          <div style={{ marginTop: '4px', color: st === 'FAILED' ? '#b00020' : '#4a5568', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '11px', maxHeight: '120px', overflow: 'auto', background: '#f7fafc', padding: '6px', borderRadius: '4px' }}>{safe(j.detail)}</div>
                        )}
                        {j.status === 'DONE' && j.result_url && (
                          <video src={j.result_url} controls style={{ width: '100%', maxWidth: '240px', borderRadius: '6px', marginTop: '6px' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TsaPersonaView() {
  return (
    <PersonaErrorBoundary>
      <PersonaProvider>
        <PersonaStudio />
      </PersonaProvider>
    </PersonaErrorBoundary>
  );
}
